import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// The mail trail of the "Red flag warnings" tab: one line per red flag, saying whether a
// warning mail went out for it, to which address, and — when it did not — why not.
//
// It is the record the feature exists to keep, so it is careful about what it claims.
// "Sent" means handed to the mail server for the address shown; it cannot show that the
// mailbox exists or that anyone read the mail, and the line above the table says so.
// "Failed" means there is no record the mail reached the employee, and the server's own words say why
// (the address was refused, the mail server could not be reached, …) and whether it will
// be tried again. When only the employee's address was refused, the people copied did get
// the mail; the row then keeps that mail and the dialog says whose it was.
// "Flag today" shows whether the red flag still stands, because a mail cannot be taken
// back once the flag behind it is corrected.
//
// The trail names employees and their addresses, so the server shows it only to a super
// admin or a role that can see all of the project's data; everyone else gets one line
// saying who can see it. The search text travels in the request body, never the URL.
// A stored mail is shown as plain text, exactly as kept — never rendered as HTML.

import { useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import Modal from '../Modal';
import TableCardWithStates from '../TableCardWithStates';
import { PageErrorBanner } from '../PageErrorBanner';
import { ModalFormErrorBanner } from '../ModalFormErrorBanner';
import { InlineLoader } from '../loading';
import {
  redFlagWarningMailsAPI,
  type RedFlagWarningTrailDetail,
  type RedFlagWarningTrailRow,
  type RedFlagWarningTrailSearch,
  type RedFlagWarningTrailState,
} from '../../services/api';
import { formatIsoDay } from '../../utils/dateHelpers';
import { apiMessage, formatMoment, httpStatus, ordinal } from './warningMailText';

const PAGE_SIZE = 50;

type StatusFilter = '' | 'sent' | 'skipped' | 'failed';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'skipped', label: 'Not sent' },
  { value: 'failed', label: 'Failed' },
];

const STATUS_BADGE: Record<RedFlagWarningTrailState, { label: string; className: string }> = {
  sent: { label: 'Sent', className: 'bg-green-50 text-green-700 border-green-200' },
  skipped: { label: 'Not sent', className: 'bg-gray-50 text-gray-600 border-gray-200' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
  sending: { label: 'Sending', className: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const WHO_CAN_SEE =
  'The mail trail is shown only to super admins and to people whose role can see all of this project’s data.';

const StatusBadge = ({ state }: { state: RedFlagWarningTrailState }) => {
  const badge = STATUS_BADGE[state] ?? STATUS_BADGE.skipped;
  return (
    <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
};

/** Why the status is what it is, in one short line (nothing to add for a mail that went out). */
const statusNote = (row: RedFlagWarningTrailRow): string | null => {
  if (row.state === 'skipped') return row.skipReasonText;
  // The server's reason. `attempts` is not a number of tries (it is also set to the
  // maximum to mean "never again"), so it is never printed.
  if (row.state === 'failed') return row.failReasonText || null;
  return null;
};

/** What the dialog says in place of, or above, the stored mail. null = nothing to add. */
const mailNote = (detail: RedFlagWarningTrailDetail): string | null => {
  if (detail.state === 'failed') {
    return detail.bodyText
      ? 'The employee’s address was refused. This is the mail the people copied received.'
      : 'There is no record that this mail reached the employee.';
  }
  if (detail.bodyText) return null;
  if (detail.state === 'sending') return 'This mail is being sent.';
  if (detail.state === 'skipped') return 'No mail was sent for this red flag.';
  return null;
};

const FlagToday = ({ value }: { value: RedFlagWarningTrailRow['flagToday'] }) => {
  if (value === 'stands') return <span className="text-gray-700">Stands</span>;
  if (value === 'withdrawn') return <span className="font-medium text-amber-700">Withdrawn</span>;
  return <span className="text-gray-400" title="Too long ago to check">—</span>;
};

const searchBody = (filter: { status: StatusFilter; q: string }, beforeId?: number): RedFlagWarningTrailSearch => ({
  limit: PAGE_SIZE,
  ...(filter.status ? { state: filter.status } : {}),
  ...(filter.q ? { q: filter.q } : {}),
  ...(beforeId !== undefined ? { beforeId } : {}),
});

export function WarningMailTrail({ projectId, canRead }: { projectId: number; canRead: boolean }) {
  const [rows, setRows] = useState<RedFlagWarningTrailRow[]>([]);
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(canRead);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);

  const [status, setStatus] = useState<StatusFilter>('');
  const [searchText, setSearchText] = useState('');
  // What the table is showing. A new object on every Search press, so pressing it again reloads.
  const [applied, setApplied] = useState<{ status: StatusFilter; q: string }>({ status: '', q: '' });

  const [openRow, setOpenRow] = useState<RedFlagWarningTrailRow | null>(null);

  // Drops an answer that belongs to an earlier search or project.
  const seq = useRef(0);

  useEffect(() => {
    if (!canRead) return;
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    redFlagWarningMailsAPI
      .searchLog(projectId, searchBody(applied))
      .then((res) => {
        if (mine !== seq.current) return;
        setRows(res.data?.data?.rows ?? []);
        setNextBeforeId(res.data?.data?.nextBeforeId ?? null);
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        setRows([]);
        setNextBeforeId(null);
        if (httpStatus(e) === 403) setRefused(true);
        else setError(apiMessage(e, 'Could not load the mail trail. Please try again.'));
      })
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
  }, [projectId, canRead, applied]);

  const loadMore = async () => {
    if (nextBeforeId === null) return;
    const mine = seq.current;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await redFlagWarningMailsAPI.searchLog(projectId, searchBody(applied, nextBeforeId));
      if (mine !== seq.current) return;
      setRows((prev) => [...prev, ...(res.data?.data?.rows ?? [])]);
      setNextBeforeId(res.data?.data?.nextBeforeId ?? null);
    } catch (e) {
      if (mine === seq.current) setError(apiMessage(e, 'Could not load more of the mail trail. Please try again.'));
    } finally {
      setLoadingMore(false);
    }
  };

  const heading = (
    <h2 id="rfw-trail-title" className="text-lg font-semibold text-gray-900">Mail trail</h2>
  );

  if (!canRead || refused) {
    return (
      <section aria-labelledby="rfw-trail-title" className="space-y-1">
        {heading}
        <p className="text-sm text-gray-500">{WHO_CAN_SEE}</p>
      </section>
    );
  }

  const filtered = applied.status !== '' || applied.q !== '';

  return (
    <section aria-labelledby="rfw-trail-title" className="space-y-3">
      <div>
        {heading}
        <p className="text-sm text-gray-500">
          Sent means handed to the mail server for the address shown. It does not show that the mailbox exists or that
          the mail was read.
        </p>
      </div>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied({ status, q: searchText.trim() });
        }}
      >
        <label className="block">
          <div className="mb-1 text-sm font-medium text-gray-700">Status</div>
          <select
            value={status}
            onChange={(e) => {
              const next = e.target.value as StatusFilter;
              setStatus(next);
              setApplied({ status: next, q: searchText.trim() });
            }}
            className="form-input"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="block min-w-[16rem] flex-1 sm:max-w-sm">
          <div className="mb-1 text-sm font-medium text-gray-700">Employee</div>
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Employee name or code"
            className="form-input"
          />
        </label>
        <button
          type="submit"
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Search className="h-4 w-4" aria-hidden />
          Search
        </button>
      </form>

      {error && <PageErrorBanner message={error} />}

      <TableCardWithStates
        loading={loading}
        isEmpty={rows.length === 0}
        emptyMessage={filtered ? 'Nothing in the mail trail matches.' : 'No red flag warning mails yet.'}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Employee</th>
                <th className="text-left px-4 py-3 font-medium">Red flag</th>
                <th className="text-left px-4 py-3 font-medium">Red flag no.</th>
                <th className="text-left px-4 py-3 font-medium">Address</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Flag today</th>
                <th className="text-center px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row) => {
                const note = statusNote(row);
                return (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">{formatMoment(row.sentAt ?? row.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{row.employeeName || '—'}</div>
                      <div className="text-xs text-gray-500">{row.employeeCode}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{row.flagLabel || row.flagType}</div>
                      <div className="text-xs text-gray-500">{formatIsoDay(row.behaviourDate)}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{row.flagNumber ? ordinal(row.flagNumber) : '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{row.toAddress || '—'}</td>
                    <td className="px-4 py-3">
                      <StatusBadge state={row.state} />
                      {note && <div className="mt-0.5 text-xs text-gray-500">{note}</div>}
                    </td>
                    <td className="px-4 py-3"><FlagToday value={row.flagToday} /></td>
                    <td className="px-4 py-3 text-center">
                      <TableActionGroup>
                        <TableActionButton action="view" label="View mail" onClick={() => setOpenRow(row)} title="View mail" />
                      </TableActionGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </TableCardWithStates>

      {nextBeforeId !== null && !loading && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Load more
          </button>
        </div>
      )}

      <TrailEntryModal projectId={projectId} row={openRow} onClose={() => setOpenRow(null)} />
    </section>
  );
}

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <>
    <dt className="text-gray-500">{label}</dt>
    <dd className="min-w-0 break-words text-gray-900">{children}</dd>
  </>
);

/** One trail line in full, with the stored mail as plain text. `row` null = closed. */
function TrailEntryModal({
  projectId, row, onClose,
}: { projectId: number; row: RedFlagWarningTrailRow | null; onClose: () => void }) {
  const [detail, setDetail] = useState<RedFlagWarningTrailDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return undefined;
    let alive = true;
    setDetail(null);
    setError(null);
    setLoading(true);
    redFlagWarningMailsAPI
      .getLogEntry(projectId, row.id)
      .then((res) => {
        if (alive) setDetail(res.data.data);
      })
      .catch((e) => {
        if (alive) setError(apiMessage(e, 'Could not open this mail. Please try again.'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [projectId, row]);

  const shown = detail ?? row;
  const note = shown ? statusNote(shown) : null;
  const aboutMail = detail ? mailNote(detail) : null;

  return (
    <Modal isOpen={row !== null} onClose={onClose} title="Red flag warning mail" size="lg">
      {shown && (
        <div className="space-y-4">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
            <Fact label="Employee">{shown.employeeName || '—'} ({shown.employeeCode})</Fact>
            <Fact label="Red flag">{shown.flagLabel || shown.flagType} · {formatIsoDay(shown.behaviourDate)}</Fact>
            {shown.flagReason && <Fact label="Why">{shown.flagReason}</Fact>}
            {shown.storeName && <Fact label="Store">{shown.storeName}</Fact>}
            <Fact label="Status">
              <StatusBadge state={shown.state} />
              {shown.sentAt && <span className="ml-2 text-gray-600">{formatMoment(shown.sentAt)}</span>}
              {note && <span className="ml-2 text-gray-600">{note}</span>}
            </Fact>
            <Fact label="Flag today"><FlagToday value={shown.flagToday} /></Fact>
            {shown.flagNumber !== null && <Fact label="Red flag no.">{ordinal(shown.flagNumber)}</Fact>}
            {/* "Sent to" only for a mail that went to it; a failed or unsent row just names the address. */}
            {shown.toAddress && <Fact label={shown.state === 'sent' ? 'Sent to' : 'Address'}>{shown.toAddress}</Fact>}
            {detail && detail.ccAddresses.length > 0 && <Fact label="Copied to">{detail.ccAddresses.join(', ')}</Fact>}
            {shown.subject && <Fact label="Subject">{shown.subject}</Fact>}
          </dl>

          {error && <ModalFormErrorBanner message={error} />}
          {loading && <InlineLoader label="Loading the mail…" className="flex" />}
          {aboutMail && <p className="text-sm text-gray-500">{aboutMail}</p>}
          {detail?.bodyText && (
            <div>
              <h4 className="mb-1 text-sm font-medium text-gray-700">The mail as sent</h4>
              <pre className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 font-sans text-sm text-gray-800">
                {detail.bodyText}
              </pre>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default WarningMailTrail;
